/* eslint-disable */
// Import Basic
import React, { useContext, useState, useEffect, useMemo } from "react";
import PropTypes from 'prop-types'
import moment from 'moment'
import 'moment/locale/es'
import 'moment/locale/pt'

// Import Material-UI
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader
} from '@material-ui/core'
import { ArrowUpward, ArrowDownward } from '@material-ui/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faComment,
  faForward,
  faCircleExclamation,
  faFlag,
  faStar
} from '@fortawesome/free-solid-svg-icons'

import AuthContext from 'contexts/AuthProvider'

// Import Internal components
import GridContainer from 'components/Grid/GridContainer'
import GridItem from 'components/Grid/GridItem'
import TextEditor from 'components/TextEditor/TextEditor.js'
import RiskSummaryV2 from '../../views/Orchestration/WhatsHappening/AuxComponents/RiskSummaryV2'
import OnePageStatusField from './OnePage-StatusField'
import StatusWarningIndicator from '../StatusWarningIndicator/StatusWarningIndicator'

// ######====== ONE PAGE | COMPONENT: CONTENT CONTAINER
// Containerized component - does NOT have local state for data; it uses the values (and update functions) passed via props.

const OnePageContentContainer = ({
  // Component Props
  isCreatingOnePage,
  currentOnePage,
  lastOnePages,
  newStatus,
  setNewStatus,
  newTendency,
  setNewTendency,
  newDate,
  setNewDate,
  newKm,
  setNewKm,
  newAch,
  setNewAch,
  newNextSteps,
  setNewNextSteps,
  newHelpRequests,
  setNewHelpRequests,
  classes,
  t,
  selectedOnePageDate,
  renderStatusColor,
  newCustomFields,
  setNewCustomFields,
  selectedInitiative,
  textEditorKey = 0, // Para forçar re-render dos TextEditors
}) => {
  const { userDetails } = useContext(AuthContext)
  
  // Estado para controlar o modal de riscos
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false)
  const [riskModalFilter, setRiskModalFilter] = useState(null)
  
  // Memoizar o filtro de riscos para evitar re-renders desnecessários
  const riskFilter = useMemo(() => ({
    initiative_id: selectedInitiative?.id,
    ...(riskModalFilter || {})
  }), [selectedInitiative?.id, riskModalFilter])

  // Funções para controlar o modal de riscos
  const handleOpenRiskModal = (filter = null) => {
    setRiskModalFilter(filter)
    setIsRiskModalOpen(true)
  }

  const handleCloseRiskModal = () => {
    setIsRiskModalOpen(false)
    setRiskModalFilter(null)
  }

  // Função para converter strings de ícones para objetos FontAwesome
  const getIconFromString = (iconValue) => {
    if (typeof iconValue === 'object') return iconValue; // Já é um objeto FontAwesome
    
    const iconMap = {
      'faComment': faComment,
      'faCircleExclamation': faCircleExclamation,
      'faForward': faForward,
      'faStar': faStar,
      'faFlag': faFlag,
      'faKey': faComment // faKey não existe, usar faComment como fallback
    };
    
    return iconMap[iconValue] || faComment; // Fallback para faComment
  }

  // ENGLISH: Define the default/fallback layout that matches the new structure.
  const defaultLayout = [
    {
      key: 'key_message',
      label_key: 'initiative_selected.one_page_key_messages',
      component: 'rich_text',
      grid_md: 12,
      icon: faComment
    },
    {
      key: 'discussion_points',
      label_key: 'initiative_selected.one_page_points_discussion',
      component: 'rich_text',
      grid_md: 12,
      icon: faCircleExclamation
    },
    {
      key: 'achievements_and_next_steps',
      label_key: 'initiative_selected.one_page_achievements_and_next_steps',
      component: 'achievements_and_next_steps',
      grid_md: 12,
      icon: faStar
    }
  ]

  // Função para converter automaticamente layouts antigos para o novo formato
  const cleanLegacyLayout = (layout) => {
    if (!layout || !Array.isArray(layout)) return defaultLayout;
    
    let processedLayout = [];
    let hasNextSteps = false;
    let hasAchievements = false;
    let hasAchievementsAndNextSteps = false;
    
    // Primeira passada: analisar o que temos
    layout.forEach(section => {
      if (section.key === 'next_steps') hasNextSteps = true;
      if (section.key === 'achievements') hasAchievements = true;
      if (section.key === 'achievements_and_next_steps') hasAchievementsAndNextSteps = true;
    });
    
    // Segunda passada: processar seções
    layout.forEach(section => {
      // Pular seções obsoletas/hardcoded
      if (section.key === 'risks' || 
          section.key === 'status_risk' || 
          section.key === 'status_and_risk_summary') {
        return; // Skip
      }
      
      // Se já tem achievements_and_next_steps, manter mas corrigir ícone
      if (section.key === 'achievements_and_next_steps') {
        processedLayout.push({
          ...section,
          icon: getIconFromString(section.icon)
        });
        return;
      }
      
      // Se tem next_steps/achievements separados E não tem achievements_and_next_steps
      if (!hasAchievementsAndNextSteps && (section.key === 'next_steps' || section.key === 'achievements')) {
        // Adicionar achievements_and_next_steps apenas uma vez
        if (section.key === 'achievements' || (section.key === 'next_steps' && !hasAchievements)) {
          processedLayout.push({
            key: 'achievements_and_next_steps',
            label_key: 'initiative_selected.one_page_achievements_and_next_steps',
            component: 'achievements_and_next_steps',
            grid_md: 12,
            icon: faStar
          });
        }
        return; // Skip as seções individuais
      }
      
      // Manter outras seções normalmente, corrigindo ícones se necessário
      const correctedSection = {
        ...section,
        icon: getIconFromString(section.icon)
      };
      processedLayout.push(correctedSection);
    });
    
    // Se não sobrou nada, usar default
    return processedLayout.length > 0 ? processedLayout : defaultLayout;
  };

  // ENGLISH: Use the custom layout if available, clean it from legacy fields, otherwise fall back to the default.
  const layout =
    userDetails.one_page_layout && userDetails.one_page_layout.length > 0
      ? cleanLegacyLayout(userDetails.one_page_layout)
      : defaultLayout

  // ENGLISH: Check if we should show the Risk Summary card.
  // If the implementation has a custom one_page_layout, we hide Risk Summary to allow full customization.
  // Status card is always shown.
  const showRiskSummary = !(userDetails.one_page_layout && userDetails.one_page_layout.length > 0)

  // Debug: Log layout being used
  console.log('OnePage Layout Debug - SOLUÇÃO DINÂMICA:', {
    hasUserLayout: userDetails.one_page_layout && userDetails.one_page_layout.length > 0,
    originalUserLayout: userDetails.one_page_layout,
    finalLayout: layout,
    isCreating: isCreatingOnePage,
    conversionApplied: userDetails.one_page_layout?.length > 0 ? 'Conversão automática aplicada' : 'Usando defaultLayout',
    layoutSource: userDetails.one_page_layout?.length > 0 ? 'banco + conversão' : 'defaultLayout'
  })

  // ENGLISH: Map field keys to their state for easy access during rendering.
  const fieldStateMap = {
    key_message: {
      value: newKm,
      setter: setNewKm,
      readOnlyValue: currentOnePage?.km
    },
    achievements: {
      value: newAch,
      setter: setNewAch,
      readOnlyValue: currentOnePage?.achievements
    },
    next_steps: {
      value: newNextSteps,
      setter: setNewNextSteps,
      readOnlyValue: currentOnePage?.next_steps
    },
    discussion_points: {
      value: newHelpRequests,
      setter: setNewHelpRequests,
      readOnlyValue: currentOnePage?.help_requests
    }
  }

  // ENGLISH: Handler for changes in custom rich text editors.
  const handleCustomFieldChange = (key, content) => {
    setNewCustomFields(prev => ({ ...prev, [key]: content }))
  }

  //Content Container Logical
  if (isCreatingOnePage) {
    return (
      <div style={{ padding: '16px 0px 0px 0px', marginBottom: '-15px' }}>
        {/* Primeira linha: Status + Tendência + Data (sem Risk Summary em edição) */}
        <GridContainer spacing={2} style={{ width: '100%', margin: '0px' }}>
          <GridItem xs={12} md={4}>
            <OnePageStatusField
              value={newStatus}
              onChange={setNewStatus}
              initiativeId={selectedInitiative?.id}
              onePageId={null} // null para nova OnePage
              classes={classes}
              t={t}
            />
          </GridItem>
          {/* Tendency */}
          <GridItem xs={12} md={4}>
            <FormControl
              fullWidth
              style={{ marginBottom: '16px' }}
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
            >
              <InputLabel style={{ color: '#888' }}>
                {t('initiative_selected.one_page_tendency')}
              </InputLabel>
              <Select
                value={newTendency}
                onChange={e => setNewTendency(e.target.value)}
                displayEmpty
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`
                }}
              >
                <MenuItem
                  value='UP'
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected
                  }}
                >
                  {t('initiative_selected.tendency_up')}
                </MenuItem>
                <MenuItem
                  value='DOWN'
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected
                  }}
                  style={{
                    alignItems: 'center'
                  }}
                >
                  {t('initiative_selected.tendency_down')}
                </MenuItem>
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={12} md={4}>
            <TextField
              label={t('initiative_selected.date')}
              type='date'
              fullWidth
              value={newDate}
              onChange={e => setNewDate(e.target.value)}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                classes: { input: classes.inputText },
                style: { fontSize: '14px' }
              }}
              InputLabelProps={{
                classes: { root: classes.labelText },
                shrink: true
              }}
              style={{ marginBottom: '16px' }}
            />
          </GridItem>
        </GridContainer>

        {/* DYNAMIC CONTENT AREA */}
        <GridContainer spacing={2} style={{ width: '100%', margin: '0px' }}>
          {layout.map(section => {
            // Tratamento especial para achievements_and_next_steps
            if (section.component === 'achievements_and_next_steps') {
              return (
                <React.Fragment key={section.key}>
                  {/* Achievements - md=6 (primeiro) */}
                  <GridItem xs={12} md={6} style={{ width: '100%' }}>
                    <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                    <CardHeader
                      title={
                        <>
                            <FontAwesomeIcon
                              icon={faStar}
                              style={{ marginRight: '8px' }}
                            />
                            {t('initiative_selected.one_page_achievements')}
                        </>
                      }
                      style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                      titleTypographyProps={{
                        style: {
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '-30px',
                          borderBottom: '1px solid #000',
                          paddingBottom: '5px'
                        }
                      }}
                    />
                      <CardContent>
                        <TextEditor
                          key={`achievements_edit_${textEditorKey}`}
                          initialContent={newAch}
                          setContent={setNewAch}
                          readOnly={false}
                          editorStyle={{
                            backgroundColor: '#fff',
                            maxHeight: '110px',
                            minHeight: '90px',
                            overflowY: 'auto',
                            padding: '10px',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none'
                          }}
                        />
                    </CardContent>
                  </Card>
                </GridItem>

                  {/* Next Steps - md=6 (segundo) */}
                  <GridItem xs={12} md={6} style={{ width: '100%' }}>
                    <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                    <CardHeader
                      title={
                        <>
                            <FontAwesomeIcon
                              icon={faForward}
                              style={{ marginRight: '8px' }}
                            />
                            {t('initiative_selected.one_page_next_steps')}
                        </>
                      }
                      style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                      titleTypographyProps={{
                        style: {
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '-30px',
                          borderBottom: '1px solid #000',
                          paddingBottom: '5px'
                        }
                      }}
                    />
                    <CardContent>
                            <TextEditor
                          key={`next_steps_edit_${textEditorKey}`}
                          initialContent={newNextSteps}
                          setContent={setNewNextSteps}
                              readOnly={false}
                              editorStyle={{
                                backgroundColor: '#fff',
                                maxHeight: '110px',
                                minHeight: '90px',
                                overflowY: 'auto',
                                padding: '10px',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none'
                              }}
                            />
                    </CardContent>
                  </Card>
                </GridItem>
                </React.Fragment>
              )
            }

            // Renderização normal para outros componentes
            const isCustom = section.key.startsWith('custom:')
            const state = isCustom
              ? {
                  value: newCustomFields[section.key] || '',
                  setter: content =>
                    handleCustomFieldChange(section.key, content)
                }
              : fieldStateMap[section.key]

            if (!state) {
              // console.warn(`OnePage: No state found for section ${section.key}`, section);
              return null; // Safety check
            }

            return (
              <GridItem xs={12} md={section.grid_md || 12} key={section.key} style={{ width: '100%' }}>
                <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                  <CardHeader
                    title={
                      <>
                        {section.icon && (
                          <FontAwesomeIcon
                            icon={section.icon}
                            style={{ marginRight: '8px' }}
                          />
                        )}
                        {t(section.label_key)}
                      </>
                    }
                    style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                    titleTypographyProps={{
                      style: {
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '-30px',
                        borderBottom: '1px solid #000',
                        paddingBottom: '5px'
                      }
                    }}
                  />
                  <CardContent>
                    <TextEditor
                      key={`${section.key}_${textEditorKey}`} // Use key to force re-render if needed
                      initialContent={state.value}
                      setContent={state.setter}
                      readOnly={false}
                      editorStyle={{
                        backgroundColor: '#fff',
                        maxHeight: '110px',
                        minHeight: '90px',
                        overflowY: 'auto',
                        padding: '10px',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none'
                      }}
                    />
                  </CardContent>
                </Card>
              </GridItem>
            )
          })}
        </GridContainer>
      </div>
    )
  }
  // Mode readonly – uses data from currentOnePage
  if (currentOnePage) {
    return (
      <div style={{ paddingTop: '16px', marginBottom: '-15px', paddingLeft: '0px', paddingRight: '0px' }}>
        <GridContainer spacing={2} style={{ marginBottom: '10px', width: '100%', margin: '0px 0px 10px 0px' }}>
          {/* Data da OnePage */}
          <GridItem xs={12} md={12} style={{ textAlign: 'left', marginBottom: '8px' }}>
            <div style={{ fontSize: '14px', color: '#333' }}>
              <strong>
                {t('initiative_selected.one_page_date')}:{' '}
              </strong>
              {currentOnePage.date ? moment(currentOnePage.date).format('DD/MM/YYYY') : ''}
            </div>
          </GridItem>
          
          {/* Initiative Leader (Sempre alinhado à esquerda) */}
          <GridItem xs={12} md={4} style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '14px', color: '#333' }}>
              <strong>
                {t('initiative_selected.one_page_initiative_leader')}:{' '}
              </strong>
              {[currentOnePage.initiative_leader, currentOnePage.initiative_leader2]
                .filter(Boolean)
                .join(', ') || ''}
            </div>
          </GridItem>

          {/* Initiative Sponsor (Centralizado a partir de md, mas à esquerda em xs/sm) */}
          <GridItem xs={12} md={4}>
            <div
              style={{
                fontSize: '14px',
                color: '#333',
                width: '100%', // Faz o texto ocupar toda a largura do GridItem
                textAlign: window.innerWidth >= 960 ? 'center' : 'left' // Alinha centro em md+ (>= 960px), senão à esquerda
              }}
            >
              <strong>
                {t('initiative_selected.one_page_initiative_sponser')}:{' '}
              </strong>
              {currentOnePage.initiative_sponser || ''}
            </div>
          </GridItem>

          {/* One Page Editor (Justificado à direita em md+, mas à esquerda em xs/sm) */}
          <GridItem xs={12} md={4}>
            <div
              style={{
                fontSize: '14px',
                color: '#333',
                width: '100%', // Ocupa toda a largura para permitir alinhamento à direita
                textAlign: window.innerWidth >= 960 ? 'right' : 'left' // Alinha à direita em md+ (>= 960px), senão à esquerda
              }}
            >
              <strong>{t('initiative_selected.one_page_editor')}: </strong>
              {currentOnePage.one_page_editor || ''}
            </div>
          </GridItem>
        </GridContainer>

        {/* Primeira linha: Status + Risk Summary (ou primeiro campo do layout) */}
        <GridContainer spacing={2} style={{ width: '100%', margin: '0px' }}>
          <GridItem
            xs={12}
            md={5}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Card
              className={classes.cardOnePage}
              elevation={0}
              style={{ width: '100%' }}
            >
                    <CardHeader
                      title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon
                      icon={faFlag}
                              style={{ marginRight: '8px' }}
                    />
                    {t('initiative_selected.one_page_status')}
                    {/* Warning indicator usando o componente padrão */}
                    {!isCreatingOnePage && 
                     currentOnePage?.status && 
                     selectedInitiative?.id && 
                     userDetails?.has_status_warning !== false && (
                      <StatusWarningIndicator
                        initiativeId={selectedInitiative.id}
                        currentStatus={currentOnePage.status}
                        size="16px"
                        color="#ff9800"
                        showTooltip={true}
                        lazyLoad={false} // ✅ VALIDAÇÃO IMEDIATA NA PÁGINA DE ONEPAGE
                      />
                    )}
                  </div>
                      }
                      style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                      titleTypographyProps={{
                        style: {
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '-30px',
                          borderBottom: '1px solid #000',
                          paddingBottom: '5px'
                        }
                      }}
                    />
              <CardContent>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    style={{
                      color: '#555',
                      fontSize: '11px',
                      marginTop: '5px'
                    }}
                  >
                    {moment(selectedOnePageDate).startOf('day').fromNow()}
                            </Typography>

                  {/* Horizontal Container:Big circle + (max 3) mini-circles*/}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {/* Container mini-circles */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {(lastOnePages || [])
                        .slice(0, 3) // pega até 3 das mais antigas
                        .map((page, idx) => (
                          <div
                            key={idx}
                            style={{
                              width: '15px',
                              height: '15px',
                              borderRadius: '50%',
                              backgroundColor: renderStatusColor(
                                page.status || 'gray'
                              )
                            }}
                          />
                        ))}
                            </div>
                    {/* BIG BALL MAIN (UNE PAGE CURRENT) */}
                            <div
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                        backgroundColor: renderStatusColor(
                          currentOnePage?.status || 'gray'
                        )
                      }}
                    />
                    {/* The trend arrow*/}
                    {currentOnePage.tendency === 'UP' ? (
                      <ArrowUpward style={{ fontSize: 22, color: '#4caf50' }} />
                    ) : currentOnePage.tendency === 'DOWN' ? (
                      <ArrowDownward
                        style={{ fontSize: 22, color: '#E04F5F' }}
                      />
                    ) : null}
                          </div>
                            </div>
                    </CardContent>
                  </Card>
                </GridItem>

          {/* Risk Summary - Condicional baseado em showRiskSummary */}
          {showRiskSummary ? (
            <GridItem
              xs={12}
              md={7}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Card
                className={classes.cardOnePage}
                elevation={0}
                style={{ width: '100%' }}
              >
                <CardHeader
                  title={
                    <>
                      <FontAwesomeIcon
                        icon={faCircleExclamation}
                        style={{ marginRight: '8px' }}
                      />
                      {t('initiative_selected.one_page_risks_summary')}
                    </>
                  }
                  style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                  titleTypographyProps={{
                    style: {
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '-30px',
                      borderBottom: '1px solid #000',
                      paddingBottom: '5px'
                    }
                  }}
                />
                <CardContent style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  padding: '0px 0px',
                  minHeight: '120px'
                }}>
                  <RiskSummaryV2
                    clientColor={userDetails.implementation_color}
                    cardHeight="fit-content"
                    isCompact={true}
                    isModalOpen={isRiskModalOpen}
                    onCloseModal={handleCloseRiskModal}
                    onOpenModalWithFilter={handleOpenRiskModal}
                    initialFilter={riskFilter}
                  />
                </CardContent>
              </Card>
            </GridItem>
          ) : (
            // Quando não há Risk Summary, renderiza o primeiro campo do layout aqui
            layout.length > 0 && (() => {
              const firstSection = layout[0]
              const isCustom = firstSection.key.startsWith('custom:')
              const readOnlyValue = isCustom
                ? currentOnePage[firstSection.key]
                : fieldStateMap[firstSection.key]?.readOnlyValue

              return (
                <GridItem xs={12} md={7} style={{ width: '100%' }}>
                  <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                    <CardHeader
                      title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {firstSection.icon && (
                            <FontAwesomeIcon
                              icon={firstSection.icon}
                              style={{ marginRight: '8px' }}
                            />
                          )}
                          {t(firstSection.label_key)}
                        </div>
                      }
                      style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                      titleTypographyProps={{
                        style: {
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '-30px',
                          borderBottom: '1px solid #000',
                          paddingBottom: '5px'
                        }
                      }}
                    />
                    <CardContent>
                      <TextEditor
                        initialContent={readOnlyValue}
                        setContent={() => {}}
                        readOnly
                        editorStyle={{
                          backgroundColor: '#fff',
                          maxHeight: '110px',
                          minHeight: '90px',
                          overflowY: 'auto',
                          padding: '10px',
                          border: 'none',
                          outline: 'none',
                          boxShadow: 'none'
                        }}
                      />
                    </CardContent>
                  </Card>
                </GridItem>
              )
            })()
          )}
        </GridContainer>

        {/* DYNAMIC CONTENT AREA */}
        <GridContainer spacing={2} style={{ width: '100%', margin: '0px' }}>
          {layout.map((section, index) => {
            // Pular o primeiro item se já foi renderizado ao lado do Status (quando showRiskSummary é false)
            if (!showRiskSummary && index === 0) {
              return null
            }

            // Tratamento especial para achievements_and_next_steps no modo readonly
            if (section.component === 'achievements_and_next_steps') {
              return (
                <React.Fragment key={section.key}>
                  {/* Achievements - md=6 (primeiro) */}
                  <GridItem xs={12} md={6} style={{ width: '100%' }}>
                    <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                      <CardHeader
                        title={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FontAwesomeIcon
                              icon={faStar}
                              style={{ marginRight: '8px' }}
                            />
                            {t('initiative_selected.one_page_achievements')}
                          </div>
                        }
                        style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                        titleTypographyProps={{
                          style: {
                            fontSize: '14px',
                            fontWeight: '500',
                            marginBottom: '-30px',
                            borderBottom: '1px solid #000',
                            paddingBottom: '5px'
                          }
                        }}
                      />
                      <CardContent>
                        <TextEditor
                          initialContent={currentOnePage?.achievements}
                          setContent={() => {}}
                          readOnly
                          editorStyle={{
                            backgroundColor: '#fff',
                            maxHeight: '110px',
                            minHeight: '90px',
                            overflowY: 'auto',
                            padding: '10px',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none'
                          }}
                        />
                      </CardContent>
                    </Card>
                  </GridItem>

                  {/* Next Steps - md=6 (segundo) */}
                  <GridItem xs={12} md={6} style={{ width: '100%' }}>
                    <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                      <CardHeader
                        title={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FontAwesomeIcon
                              icon={faForward}
                              style={{ marginRight: '8px' }}
                            />
                            {t('initiative_selected.one_page_next_steps')}
                          </div>
                        }
                        style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                        titleTypographyProps={{
                          style: {
                            fontSize: '14px',
                            fontWeight: '500',
                            marginBottom: '-30px',
                            borderBottom: '1px solid #000',
                            paddingBottom: '5px'
                          }
                        }}
                      />
                      <CardContent>
                        <TextEditor
                          initialContent={currentOnePage?.next_steps}
                          setContent={() => {}}
                          readOnly
                          editorStyle={{
                            backgroundColor: '#fff',
                            maxHeight: '110px',
                            minHeight: '90px',
                            overflowY: 'auto',
                            padding: '10px',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none'
                          }}
                        />
                      </CardContent>
                    </Card>
                  </GridItem>
                </React.Fragment>
              )
            }

            // Renderização normal para outros componentes
            const isCustom = section.key.startsWith('custom:')
            const readOnlyValue = isCustom
              ? currentOnePage[section.key]
              : fieldStateMap[section.key]?.readOnlyValue

            return (
              <GridItem xs={12} md={section.grid_md || 12} key={section.key} style={{ width: '100%' }}>
                <Card className={classes.cardOnePage} elevation={0} style={{ width: '100%' }}>
                  <CardHeader
                    title={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {section.icon && (
                          <FontAwesomeIcon
                            icon={section.icon}
                            style={{ marginRight: '8px' }}
                          />
                        )}
                        {t(section.label_key)}
                      </div>
                    }
                    style={{ backgroundColor: '#fff', marginBottom: '-3px' }}
                    titleTypographyProps={{
                      style: {
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '-30px',
                        borderBottom: '1px solid #000',
                        paddingBottom: '5px'
                      }
                    }}
                  />
                  <CardContent>
                    <TextEditor
                      initialContent={readOnlyValue}
                      setContent={() => {}}
                      readOnly
                      editorStyle={{
                        backgroundColor: '#fff',
                        maxHeight: '110px',
                        minHeight: '90px',
                        overflowY: 'auto',
                        padding: '10px',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none'
                      }}
                    />
                  </CardContent>
                </Card>
              </GridItem>
            )
          })}
        </GridContainer>
      </div>
    )
  }
  // If no data selected
  return (
    <Typography
      variant='body1'
      style={{
        color: '#555',
        marginTop: '16px',
        textAlign: 'center',
        fontStyle: 'italic'
      }}
    >
      {t('initiative_selected.one_page_no_data')}
    </Typography>
  )
}

// ### PROPS TYPE VALIDATION AND EXPORT DEFAULT
OnePageContentContainer.propTypes = {
  isCreatingOnePage: PropTypes.bool.isRequired,
  currentOnePage: PropTypes.object,
  selectedInitiative: PropTypes.object,
  newStatus: PropTypes.string.isRequired,
  setNewStatus: PropTypes.func.isRequired,
  newTendency: PropTypes.string.isRequired,
  setNewTendency: PropTypes.func.isRequired,
  newDate: PropTypes.string.isRequired,
  setNewDate: PropTypes.func.isRequired,
  newKm: PropTypes.string.isRequired,
  setNewKm: PropTypes.func.isRequired,
  newAch: PropTypes.string.isRequired,
  setNewAch: PropTypes.func.isRequired,
  newNextSteps: PropTypes.string.isRequired,
  setNewNextSteps: PropTypes.func.isRequired,
  newHelpRequests: PropTypes.string.isRequired,
  setNewHelpRequests: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  lastOnePages: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      status: PropTypes.string
    })
  ),
  selectedOnePageDate: PropTypes.string.isRequired,
  renderStatusColor: PropTypes.func.isRequired,
  newCustomFields: PropTypes.object.isRequired,
  setNewCustomFields: PropTypes.func.isRequired,
  textEditorKey: PropTypes.number
}

export default OnePageContentContainer